<template>
  <div class="basket-promocode">
    <!-- Кнопка раскрытия промокода -->
    <div
      class="grid grid-cols-[20px_1fr_auto] gap-5 items-center p-5 cursor-pointer"
      @click="togglePromocode"
    >
      <UIcon name="i-heroicons-ticket-16-solid" class="w-5 h-5" />
      <p>Промокод</p>
      <UIcon
        name="i-heroicons-chevron-right-20-solid"
        class="w-5 h-5 transition-all ease-in-out duration-300"
        :class="{ 'transform rotate-90': isExpanded }"
      />
    </div>
    
    <!-- Форма промокода -->
    <div v-if="isExpanded" class="p-5 pt-0">
      <div class="flex items-stretch gap-2">
        <UiKitInput
          v-model="localPromocode"
          class="flex-1 min-w-0 promocode-input"
          :disabled="isApplied || isLoading"
          placeholder="Введите промокод"
          @keyup.enter="handleApply"
          @input="handleInput"
        />

        <UiKitButton
          v-if="!isApplied"
          color="success"
          size="md"
          class="h-11 w-32 shrink-0"
          :disabled="isLoading || !localPromocode.trim()"
          :loading="isLoading"
          data-testid="promocode-apply"
          @click="handleApply"
        >
          Применить
        </UiKitButton>

        <UiKitButton
          v-else
          color="danger"
          size="md"
          class="h-11 w-32 shrink-0"
          data-testid="promocode-remove"
          @click="handleRemove"
        >
          Удалить
        </UiKitButton>
      </div>

      <!-- Причина отказа: не авторизован, промокод не найден/истёк, не добрана
           минимальная сумма. Без этого блока любой отказ выглядит как «кнопка не
           работает»: usePromocode выставляет error, а показать его было негде. -->
      <div
        v-if="error"
        class="mt-3 text-sm rounded-lg px-3 py-2 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30"
        data-testid="promocode-error"
      >
        {{ error }}
      </div>

      <!-- Взаимоисключение: промокод гасит баллы и приветственную скидку.
           Показываем информативную подсказку, какую выгоду промокод перекрывает. -->
      <div
        v-if="conflictHint"
        class="mt-3 text-sm rounded-lg px-3 py-2 text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30"
        data-testid="promocode-conflict-hint"
      >
        {{ conflictHint }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'

interface Props {
  promocode: string
  isApplied: boolean
  isLoading: boolean
  error: string | null
  discountAmount: number
  isFreeDelivery: boolean
  modelValue?: boolean // ✅ Добавлен prop для управления раскрытием извне
  /** Промокод несовместим со списанием баллов (compatibleWithLoyalty=false). */
  incompatibleWithLoyalty?: boolean
  /** Размер приветственной скидки, которую гасит применённый промокод (₽). */
  onboardingSuppressedDiscount?: number
}

interface Emits {
  (e: 'update:promocode', value: string): void
  (e: 'update:modelValue', value: boolean): void // ✅ Добавлен emit для двустороннего связывания
  (e: 'apply'): void
  (e: 'remove'): void
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: false, // По умолчанию свернут
  incompatibleWithLoyalty: false,
  onboardingSuppressedDiscount: 0,
})
const emit = defineEmits<Emits>()

const localPromocode = ref(props.promocode)

/**
 * Информативная подсказка о взаимоисключении (как в блоке баллов).
 * Можно применить что-то одно: промокод гасит баллы и приветственную скидку.
 * Приоритет сообщения: несовместимость с баллами важнее, затем — погашенная скидка.
 */
const conflictHint = computed<string | null>(() => {
  if (!props.isApplied) return null

  if (props.incompatibleWithLoyalty) {
    return 'Списание баллов не действует с этим промокодом. Удалите промокод, чтобы списать баллы.'
  }

  if (props.onboardingSuppressedDiscount > 0) {
    return `Приветственная скидка −${props.onboardingSuppressedDiscount} ₽ не действует с промокодом. `
      + 'Удалите промокод, чтобы получить скидку.'
  }

  return null
})

/**
 * Реактивная ссылка на состояние раскрытия
 * ✅ Синхронизируется с prop modelValue для управления извне
 */
const isExpanded = ref(props.modelValue)

watch(() => props.promocode, (newValue) => {
  localPromocode.value = newValue
})

// ✅ Синхронизация isExpanded с prop modelValue
watch(() => props.modelValue, (newValue) => {
  isExpanded.value = newValue
})

// ✅ Emit изменения isExpanded для двустороннего связывания
watch(isExpanded, (newValue) => {
  emit('update:modelValue', newValue)
})

/**
 * Переключает видимость блока промокода
 */
function togglePromocode(): void {
  isExpanded.value = !isExpanded.value
}

function handleInput(event: Event): void {
  const target = event.target as HTMLInputElement
  const value = target.value.toUpperCase()
  localPromocode.value = value
  emit('update:promocode', value)
}

function handleApply(): void {
  if (localPromocode.value.trim()) {
    emit('apply')
  }
}

function handleRemove(): void {
  localPromocode.value = ''
  emit('update:promocode', '')
  emit('remove')
}
</script>

<style scoped lang="scss">
.basket-promocode {
  @apply mb-4;
}

// Ввод промокода капсом, плейсхолдер — обычным регистром
.promocode-input :deep(input) {
  text-transform: uppercase;

  &::placeholder {
    text-transform: none;
  }
}
</style>

